<script setup>
import { ref, computed, getCurrentInstance } from 'vue';
import { router, usePage } from '@inertiajs/vue3';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import { useLoading } from '@/composables/useLoading';
import { useToast } from 'primevue/usetoast';
import Divider from 'primevue/divider';
import RecipientContactBlock from '@/components/admin/orders/RecipientContactBlock.vue';
import SiteFeaturesInfo from '@/components/SiteFeaturesInfo.vue';
import ClientEmailMessagePanel from '@/components/admin/ClientEmailMessagePanel.vue';

defineOptions({
    layout: AdminMainLayout,
});

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const { showLoading, hideLoading } = useLoading();
const toast = useToast();
const page = usePage();

const props = defineProps({
    order: Object,
    itemsWithContent: Array,
    allConfirmed: Boolean,
    hasPublishedContent: Boolean,
});

// Store live link for each item
const liveLinks = ref({});
const sendEmailToClient = ref(true);
const clientEmailOptions = ref({ message: null, locale: null });
const showErrorModal = ref(false);
const errorMessage = ref('');

// Reject content variables
const showRejectModal = ref(false);
const selectedRejectContentId = ref(null);
const rejectReason = ref('');
const rejectingContent = ref(false);

// Store expansion state for each item
const expandedItems = ref({});

// Function to translate error codes from backend
const translateError = (errorCode) => {
    // Domain validation errors
    if (errorCode.startsWith('domain_mismatch:')) {
        const parts = errorCode.split(':');
        const correctDomain = parts[1];
        const providedDomain = parts[2] || '';
        return $t('The live link domain does not match the purchased site domain') +
               ` (${$t('Expected')}: ${correctDomain}, ${$t('Provided')}: ${providedDomain})`;
    } else if (errorCode.startsWith('domain_similar:')) {
        const parts = errorCode.split(':');
        const correctDomain = parts[1];
        const providedDomain = parts[2] || '';
        return $t('The live link domain is similar to but not identical to the purchased domain') +
               ` (${$t('Expected')}: ${correctDomain}, ${$t('Provided')}: ${providedDomain})`;
    } else if (errorCode === 'domain_mismatch') {
        return $t('The live link domain must match the purchased site domain');
    }

    // Web scraping errors
    else if (errorCode.startsWith('scraping_failed:')) {
        const statusCode = errorCode.split(':')[1];
        return $t('Failed to access the live link page') + ` (${$t('Status')}: ${statusCode})`;
    } else if (errorCode.startsWith('scraping_exception:')) {
        return $t('Error occurred while checking the live link page');
    }

    // Anchor tag not found errors
    else if (errorCode.startsWith('anchor_not_found|')) {
        const parts = errorCode.split('|');
        const anchorName = parts[1] || '';
        const anchorUrl = parts[2] || '';
        return $t('Neither anchor text nor URL were found on the page') +
               ` (${$t('Text')}: "${anchorName}", ${$t('URL')}: "${anchorUrl}")`;
    }

    // Exact matches not found but similar found
    else if (errorCode.startsWith('anchor_name_similar|')) {
        const parts = errorCode.split('|');
        const expectedName = parts[1] || '';
        const foundSimilar = parts[2] || '';
        return $t('Exact anchor text not found, but similar text was found') +
               ` (${$t('Expected')}: "${expectedName}", ${$t('Found similar')}: "${foundSimilar}")`;
    } else if (errorCode.startsWith('anchor_url_similar|')) {
        const parts = errorCode.split('|');
        const expectedUrl = parts[1] || '';
        const foundSimilar = parts[2] || '';
        return $t('Exact anchor URL not found, but similar URL was found') +
               ` (${$t('Expected')}: "${expectedUrl}", ${$t('Found similar')}: "${foundSimilar}")`;
    }

    // Both similar
    else if (errorCode.startsWith('anchor_both_similar|')) {
        const parts = errorCode.split('|');
        const expectedName = parts[1] || '';
        const foundSimilarName = parts[2] || '';
        const expectedUrl = parts[3] || '';
        const foundSimilarUrl = parts[4] || '';
        return $t('Exact anchor tag not found, but similar text and URL were found') +
               ` (${$t('Expected text')}: "${expectedName}", ${$t('Found similar text')}: "${foundSimilarName}", ` +
               `${$t('Expected URL')}: "${expectedUrl}", ${$t('Found similar URL')}: "${foundSimilarUrl}")`;
    }

    // Exact not found errors (no similar found)
    else if (errorCode.startsWith('anchor_name_not_found|')) {
        const parts = errorCode.split('|');
        const expectedName = parts[1] || '';
        const status = parts[2] || '';
        if (status === 'not_found') {
            return $t('Anchor text not found on the page') +
                   ` (${$t('Expected')}: "${expectedName}", ${$t('Found')}: ${$t('Nothing')})`;
        } else {
            return $t('Anchor text not found on the page') + ` ("${expectedName}")`;
        }
    } else if (errorCode.startsWith('anchor_url_not_found|')) {
        const parts = errorCode.split('|');
        const expectedUrl = parts[1] || '';
        const status = parts[2] || '';
        if (status === 'not_found') {
            return $t('Anchor URL not found on the page') +
                   ` (${$t('Expected')}: "${expectedUrl}", ${$t('Found')}: ${$t('Nothing')})`;
        } else {
            return $t('Anchor URL not found on the page') + ` ("${expectedUrl}")`;
        }
    }

    // General anchor mismatch
    else if (errorCode === 'anchor_mismatch') {
        return $t('Anchor tag found but does not match the required text and URL');
    }

    // Validation exception
    else if (errorCode.startsWith('validation_exception:')) {
        return $t('Validation error occurred. Please try again.');
    }

    // Default
    return errorCode;
};

// Function to determine error severity for styling
const getErrorSeverity = (errorCode) => {
    // Similar errors (warnings) - Yellow
    if (errorCode.startsWith('domain_similar:') ||
        errorCode.startsWith('anchor_name_similar|') ||
        errorCode.startsWith('anchor_url_similar|') ||
        errorCode.startsWith('anchor_both_similar|')) {
        return 'warn';
    }

    // All other errors (critical) - Red
    return 'error';
};

const openErrorModal = (message) => {
    errorMessage.value = message;
    showErrorModal.value = true;
};

const closeErrorModal = () => {
    showErrorModal.value = false;
    errorMessage.value = '';
};

// Initialize live links with existing values
props.itemsWithContent?.forEach(item => {
    liveLinks.value[item.id] = item.link || '';
});

const postLiveLink = (itemId, skipValidation = false) => {
    const liveLink = liveLinks.value[itemId];

    if (!liveLink || !liveLink.trim()) {
        openErrorModal($t('Please enter a valid live link URL'));
        return;
    }

    try {
        new URL(liveLink);
    } catch (e) {
        openErrorModal($t('Please enter a valid URL (must start with http:// or https://)'));
        return;
    }

    router.post(
        proxy.route ? proxy.route('order-item.live-link.confirm', itemId) : `/admin/order-item/${itemId}/live-link`,
        {
            link: liveLink,
            send_email_to_client: sendEmailToClient.value,
            skip_validation: skipValidation,
            client_message: clientEmailOptions.value.message,
            email_locale: clientEmailOptions.value.locale,
        },
        {
            preserveScroll: true,
            preserveState: true,
            onStart: () => showLoading($t('Confirming live link...')),
            onFinish: () => hideLoading(),
            onError: () => hideLoading(),
        }
    );
};

const confirmLiveLink = (itemId) => postLiveLink(itemId, false);
const confirmLiveLinkSkipValidation = (itemId) => postLiveLink(itemId, true);

const goToOrder = () => {
    router.visit(
        proxy.route ? proxy.route('orders.index') : `/admin/orders`,
        {
            onStart: () => showLoading($t('Loading order...')),
            onFinish: () => hideLoading(),
        }
    );
};

const itemsCount = computed(() => props.itemsWithContent?.length || 0);
const confirmedCount = computed(() => props.itemsWithContent?.filter(item => item.live_link_confirmed_at !== null).length || 0);

const goToEditOrder = () => router.visit(proxy.route ? proxy.route('orders.edit', props.order.id) : `/admin/orders/${props.order.id}/edit`);

// Reject content functions
const openRejectModal = (contentId) => {
    selectedRejectContentId.value = contentId;
    rejectReason.value = '';
    showRejectModal.value = true;
};

const closeRejectModal = () => {
    showRejectModal.value = false;
    selectedRejectContentId.value = null;
    rejectReason.value = '';
};

const rejectContent = () => {
    if (!selectedRejectContentId.value) return;

    const contentId = selectedRejectContentId.value;
    const reason = rejectReason.value.trim() || null;
    closeRejectModal();

    router.post(
        proxy.route ? proxy.route('order-content.reject', contentId) : `/admin/order-content/${contentId}/reject`,
        { rejection_reason: reason },
        {
            preserveScroll: true,
            preserveState: true,
            onStart: () => { rejectingContent.value = true; },
            onFinish: () => { rejectingContent.value = false; },
            onSuccess: () => {
                toast.add({
                    severity: 'success',
                    summary: $t('Success'),
                    detail: $t('Content rejected successfully. The client has been notified.'),
                    life: 5000
                });
            },
            onError: (errors) => {
                toast.add({
                    severity: 'error',
                    summary: $t('Error'),
                    detail: errors?.error || errors?.rejection_reason?.[0] || $t('Failed to reject content'),
                    life: 5000
                });
            },
        }
    );
};

const toggleItemExpansion = (itemId) => {
    expandedItems.value[itemId] = !expandedItems.value[itemId];
};
</script>

<template>
    <div class="grid">
        <!-- Header Card -->
        <Card class="mb-6">
            <template #header>
                <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between p-6 pb-0 gap-4">
                    <div>
                        <h1 class="text-2xl font-bold text-surface-900">
                            {{ $t('Live Link Confirmation') }}
                        </h1>
                        <p class="text-surface-600 mt-1">
                            {{ $t('Order') }} #{{ order.number }} - {{ order.client?.name }}
                        </p>
                    </div>
                    <div class="flex items-center gap-3">
                        <Button
                            :label="$t('Edit order')"
                            icon="pi pi-pencil"
                            size="small"
                            outlined
                            @click="goToEditOrder"
                        />
                        <Button
                            :label="$t('Back to Orders')"
                            icon="pi pi-arrow-left"
                            severity="secondary"
                            @click="goToOrder"
                        />
                    </div>
                </div>
            </template>

            <template #content>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div class="p-4 bg-surface-50 rounded-lg border border-surface-200">
                        <div class="flex items-center gap-3">
                            <div class="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center">
                                <i class="pi pi-list text-white text-xl"></i>
                            </div>
                            <div>
                                <p class="text-sm text-surface-600">{{ $t('Published Content') }}</p>
                                <p class="text-2xl font-bold text-surface-900">{{ itemsCount }}</p>
                            </div>
                        </div>
                    </div>

                    <div class="p-4 bg-surface-50 rounded-lg border border-surface-200">
                        <div class="flex items-center gap-3">
                            <div class="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
                                <i class="pi pi-check text-white text-xl"></i>
                            </div>
                            <div>
                                <p class="text-sm text-surface-600">{{ $t('Live Links Confirmed') }}</p>
                                <p class="text-2xl font-bold text-surface-900">{{ confirmedCount }}/{{ itemsCount }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </Card>

        <!-- No Published Content Message -->
        <Card v-if="!hasPublishedContent" class="mb-6">
            <template #content>
                <div class="text-center py-12">
                    <i class="pi pi-inbox text-6xl text-surface-300 mb-4"></i>
                    <h3 class="text-xl font-semibold text-surface-700 mb-2">
                        {{ $t('No published content available') }}
                    </h3>
                    <p class="text-surface-500">
                        {{ $t('Content must be sent to the portal before live links can be submitted.') }}
                    </p>
                </div>
            </template>
        </Card>

        <!-- Client email message customization -->
        <ClientEmailMessagePanel
            v-if="sendEmailToClient && hasPublishedContent"
            email-type="live_links_published"
            :client-locale="order.client?.locale || 'en'"
            class="mb-4"
            @change="payload => clientEmailOptions = payload"
        />

        <!-- Items List -->
        <div v-if="hasPublishedContent" class="space-y-4">
            <Card v-for="(item, index) in itemsWithContent" :key="item.id">
                <template #header>
                    <div class="p-6 pb-0">
                        <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                            <div class="flex items-center gap-3">
                                <span class="font-bold text-surface-900">{{ index + 1 }}</span>
                                <div>
                                    <h3 class="font-semibold text-surface-900">
                                        {{ item.site_url || $t('Site URL not available') }}
                                    </h3>
                                    <p class="text-sm text-surface-600">
                                        {{ item.type_name || '-' }}
                                    </p>
                                </div>
                            </div>
                            <div class="flex items-center gap-3">
                                <Tag v-if="item.canceled_at" :value="$t('Canceled')" severity="danger" />
                                <Tag
                                    v-if="item.content && item.content.rejected_at"
                                    :value="$t('Content Rejected')"
                                    severity="danger"
                                    icon="pi pi-times"
                                />
                                <Tag
                                    v-else-if="item.live_link_confirmed_at && item.live_link_validation_skipped"
                                    :value="$t('Live Link Confirmed')"
                                    severity="success"
                                    icon="pi pi-check"
                                />
                                <Tag
                                    v-if="item.live_link_validation_skipped"
                                    :value="$t('Validation skipped')"
                                    severity="warn"
                                    icon="pi pi-exclamation-triangle"
                                />
                                <Tag
                                    v-if="item.content && item.content.skipped_validation"
                                    :value="$t('Content without validation')"
                                    severity="warn"
                                    icon="pi pi-exclamation-triangle"
                                />
                                <Tag
                                    v-else-if="item.live_link_confirmed_at && !item.live_link_validation_skipped"
                                    :value="$t('Live Link Confirmed')"
                                    severity="success"
                                    icon="pi pi-check"
                                />
                                <Tag
                                    v-else
                                    :value="$t('Pending Live Link')"
                                    severity="warn"
                                    icon="pi pi-clock"
                                />

                                <Button
                                    :icon="expandedItems[item.id] ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"
                                    :label="$t('Show')"
                                    size="small"
                                    severity="secondary"
                                    outlined
                                    @click="toggleItemExpansion(item.id)"
                                />
                            </div>
                        </div>
                    </div>
                </template>

                <template #content>
                    <div v-show="expandedItems[item.id]">
                        <div v-if="item.content" class="space-y-4">
                        <!-- Site Information Section -->
                        <SiteFeaturesInfo v-if="item.site_features" :features="item.site_features" :accepted-anchors="item.site_features?.accepted_anchors || item.accepted_anchors || 1" />

                        <!-- Anchors Section -->
                        <div v-if="item.content.anchor_name || item.content.anchor_url || item.content.anchor_name_2 || item.content.anchor_url_2 || item.content.anchor_name_3 || item.content.anchor_url_3"
                             class="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <h5 class="text-sm font-semibold text-blue-900 mb-3">
                                <i class="pi pi-link text-blue-700 mr-2"></i>
                                {{ $t('Anchors') }}
                            </h5>

                            <div class="flex flex-col md:flex-row gap-4">
                                <!-- Anchor 1 -->
                                <div v-if="item.content.anchor_name || item.content.anchor_url" class="flex-1">
                                    <div class="space-y-2">
                                        <div>
                                            <label class="block text-xs font-medium text-blue-800 mb-1">
                                                {{ $t('Anchor Name') }} 1
                                            </label>
                                            <p class="text-sm text-blue-900">{{ item.content.anchor_name || '-' }}</p>
                                        </div>
                                        <div>
                                            <label class="block text-xs font-medium text-blue-800 mb-1">
                                                {{ $t('Anchor URL') }} 1
                                            </label>
                                            <p class="text-xs text-blue-900 break-all">{{ item.content.anchor_url || '-' }}</p>
                                        </div>
                                    </div>
                                </div>

                                <!-- Divider -->
                                <Divider v-if="(item.content.anchor_name || item.content.anchor_url) && (item.content.anchor_name_2 || item.content.anchor_url_2)" layout="vertical" class="hidden md:flex" />

                                <!-- Anchor 2 -->
                                <div v-if="item.content.anchor_name_2 || item.content.anchor_url_2" class="flex-1">
                                    <div class="space-y-2">
                                        <div>
                                            <label class="block text-xs font-medium text-blue-800 mb-1">
                                                {{ $t('Anchor Name') }} 2
                                            </label>
                                            <p class="text-sm text-blue-900">{{ item.content.anchor_name_2 || '-' }}</p>
                                        </div>
                                        <div>
                                            <label class="block text-xs font-medium text-blue-800 mb-1">
                                                {{ $t('Anchor URL') }} 2
                                            </label>
                                            <p class="text-xs text-blue-900 break-all">{{ item.content.anchor_url_2 || '-' }}</p>
                                        </div>
                                    </div>
                                </div>

                                <!-- Divider -->
                                <Divider v-if="(item.content.anchor_name_2 || item.content.anchor_url_2) && (item.content.anchor_name_3 || item.content.anchor_url_3)" layout="vertical" class="hidden md:flex" />

                                <!-- Anchor 3 -->
                                <div v-if="item.content.anchor_name_3 || item.content.anchor_url_3" class="flex-1">
                                    <div class="space-y-2">
                                        <div>
                                            <label class="block text-xs font-medium text-blue-800 mb-1">
                                                {{ $t('Anchor Name') }} 3
                                            </label>
                                            <p class="text-sm text-blue-900">{{ item.content.anchor_name_3 || '-' }}</p>
                                        </div>
                                        <div>
                                            <label class="block text-xs font-medium text-blue-800 mb-1">
                                                {{ $t('Anchor URL') }} 3
                                            </label>
                                            <p class="text-xs text-blue-900 break-all">{{ item.content.anchor_url_3 || '-' }}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Other Content Details -->
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div v-if="item.content">
                                <label class="block text-sm font-medium text-surface-700 mb-1">
                                    {{ $t('Language') }}
                                </label>
                                <p class="text-surface-900">{{ item.content.language || '-' }}</p>
                            </div>

                            <div v-if="item.content">
                                <label class="block text-sm font-medium text-surface-700 mb-1">
                                    {{ $t('Sent to Portal At') }}
                                </label>
                                <p class="text-surface-900">
                                    {{ item.content.sent_to_portal_at ? new Date(item.content.sent_to_portal_at).toLocaleString() : '-' }}
                                </p>
                            </div>
                        </div>

                        <!-- Seller & Owner Details -->
                        <RecipientContactBlock
                            v-if="item.publication_recipient"
                            :recipient="item.publication_recipient"
                            :read-only="true"
                        />

                        <!-- Observations -->
                        <div v-if="item.observations" class="p-4 bg-amber-50 rounded-lg border border-amber-200">
                            <label class="block text-sm font-medium text-amber-900 mb-2">
                                <i class="pi pi-info-circle mr-1"></i>
                                {{ $t('Observations') }}
                            </label>
                            <p class="text-surface-900">{{ item.observations }}</p>
                        </div>

                        <!-- Rejection Status -->
                        <div v-if="item.content && item.content.rejected_at" class="p-4 bg-red-50 rounded-lg border border-red-200">
                            <div class="flex items-center gap-2 text-red-700 mb-3">
                                <i class="pi pi-times-circle text-xl"></i>
                                <div>
                                    <p class="font-medium">{{ $t('Content rejected') }}</p>
                                    <p class="text-sm">
                                        {{ new Date(item.content.rejected_at).toLocaleString() }}
                                    </p>
                                </div>
                            </div>

                            <div v-if="item.content.rejection_reason" class="mb-3">
                                <label class="block text-sm font-medium text-red-900 mb-1">
                                    {{ $t('Rejection reason') }}:
                                </label>
                                <p class="text-sm text-red-800">{{ item.content.rejection_reason }}</p>
                            </div>

                            <div class="p-3 bg-amber-50 rounded-lg border border-amber-200">
                                <div class="flex items-center gap-2 text-amber-700">
                                    <i class="pi pi-clock text-lg"></i>
                                    <p class="text-sm font-medium">{{ $t('Waiting for client to revise and resubmit content') }}</p>
                                </div>
                            </div>
                        </div>

                        <!-- Live Link Confirmed Status -->
                        <div v-else-if="item.live_link_confirmed_at" class="p-4 bg-green-50 rounded-lg border border-green-200">
                            <div class="flex flex-col gap-2">
                                <div class="flex items-center gap-2 text-green-700">
                                    <i class="pi pi-check-circle text-xl"></i>
                                    <div>
                                        <p class="font-medium">{{ $t('Live link confirmed') }}</p>
                                        <p class="text-sm">
                                            {{ new Date(item.live_link_confirmed_at).toLocaleString() }}
                                        </p>
                                    </div>
                                </div>
                                <div class="mt-2">
                                    <label class="block text-sm font-medium text-green-700 mb-1">
                                        {{ $t('Live Link URL') }}
                                    </label>
                                    <a
                                        :href="item.link"
                                        target="_blank"
                                        class="text-primary-500 hover:text-primary-600 underline break-all"
                                    >
                                        {{ item.link }}
                                    </a>
                                </div>
                            </div>
                        </div>

                        <!-- Live Link Input and Confirm Button -->
                        <div v-else class="space-y-3">
                            <div class="p-4 bg-blue-50 rounded-lg border border-blue-200">
                                <label class="block text-sm font-medium text-surface-700 mb-2">
                                    {{ $t('Enter Live Link URL') }} <span class="text-red-500">*</span>
                                </label>
                                <div class="relative">
                                    <InputText
                                        v-model="liveLinks[item.id]"
                                        type="url"
                                        placeholder="https://example.com/article"
                                        class="w-full"
                                        :class="{
                                            'p-invalid': page.props.errors?.link
                                        }"
                                        :disabled="item.content && item.content.rejected_at && !item.content.revised_at"
                                    />
                                </div>
                                <small class="text-surface-500 mt-1 block">
                                    {{ $t('Enter the full URL where the content was published') }}
                                </small>

                                <!-- Error Messages -->
                                <div v-if="page.props.errors?.link" class="mt-2">
                                    <small
                                        v-for="(error, index) in (Array.isArray(page.props.errors.link) ? page.props.errors.link : [page.props.errors.link])"
                                        :key="index"
                                        :class="{
                                            'text-amber-600': getErrorSeverity(error) === 'warn',
                                            'text-red-500': getErrorSeverity(error) === 'error'
                                        }"
                                        class="block font-medium"
                                    >
                                        <i
                                            :class="{
                                                'pi pi-exclamation-triangle text-amber-500': getErrorSeverity(error) === 'warn',
                                                'pi pi-times-circle text-red-500': getErrorSeverity(error) === 'error'
                                            }"
                                            class="mr-1"
                                        ></i>
                                        {{ translateError(error) }}
                                    </small>
                                </div>
                            </div>

                            <div class="flex flex-wrap items-center justify-end gap-3">
                                <div class="flex items-center gap-2 mr-auto">
                                    <Checkbox v-model="sendEmailToClient" :inputId="`sendEmailToClient-${item.id}`" :binary="true" />
                                    <label :for="`sendEmailToClient-${item.id}`" class="text-sm text-surface-700 cursor-pointer select-none">
                                        {{ $t('Send email to client') }}
                                    </label>
                                </div>
                                <Button
                                    :label="$t('Confirm without validation')"
                                    icon="pi pi-exclamation-triangle"
                                    severity="warn"
                                    outlined
                                    @click="confirmLiveLinkSkipValidation(item.id)"
                                    :disabled="!liveLinks[item.id] || !liveLinks[item.id].trim() || (item.content && item.content.rejected_at && !item.content.revised_at)"
                                />
                                <Button
                                    :label="$t('Reject')"
                                    icon="pi pi-times"
                                    severity="danger"
                                    outlined
                                    :disabled="item.content && item.content.rejected_at && !item.content.revised_at"
                                    @click="openRejectModal(item.content.id)"
                                />
                                <Button
                                    :label="$t('Confirm Live Link')"
                                    icon="pi pi-link"
                                    severity="success"
                                    @click="confirmLiveLink(item.id)"
                                    :disabled="!liveLinks[item.id] || !liveLinks[item.id].trim() || (item.content && item.content.rejected_at && !item.content.revised_at) || (item.publication_recipient && !item.publication_recipient.type)"
                                />
                            </div>
                        </div>
                        </div>
                    </div>
                </template>
            </Card>
        </div>

        <!-- All Confirmed Message -->
        <Card v-if="allConfirmed && hasPublishedContent" class="mt-6">
            <template #content>
                <div class="text-center py-8">
                    <div class="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                        <i class="pi pi-check text-green-600 text-3xl"></i>
                    </div>
                    <h3 class="text-xl font-semibold text-surface-900 mb-2">
                        {{ $t('All live links confirmed!') }}
                    </h3>
                    <p class="text-surface-600">
                        {{ $t('All live links have been confirmed. The order is now published.') }}
                    </p>
                </div>
            </template>
        </Card>
    </div>

    <Dialog
        v-model:visible="showErrorModal"
        :header="$t('URL Validation Error')"
        :modal="true"
        :closable="true"
        :style="{ width: '30vw' }"
        :breakpoints="{ '960px': '75vw', '640px': '95vw' }"
        @hide="closeErrorModal"
    >
        <div class="space-y-4">
            <div class="flex items-start gap-3">
                <div class="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <i class="pi pi-exclamation-triangle text-red-600 text-xl"></i>
                </div>
                <div class="flex-1">
                    <h3 class="text-lg font-semibold text-surface-900 mb-2">
                        {{ $t('Invalid URL') }}
                    </h3>
                    <p class="text-surface-700">
                        {{ errorMessage }}
                    </p>
                </div>
            </div>
        </div>

        <template #footer>
            <div class="flex justify-end">
                <Button
                    :label="$t('Close')"
                    icon="pi pi-times"
                    severity="secondary"
                    outlined
                    @click="closeErrorModal"
                />
            </div>
        </template>
    </Dialog>

    <!-- Reject Content Modal -->
    <Dialog
        v-model:visible="showRejectModal"
        modal
        :header="$t('Reject Content')"
        :style="{ width: '500px' }"
        @hide="closeRejectModal"
    >
        <div class="space-y-4">
            <p class="text-surface-700">
                {{ $t('Please provide feedback for the content rejection. This will help the client understand what needs to be revised.') }}
            </p>

            <div class="space-y-2">
                <label for="rejectReason" class="block text-sm font-medium text-surface-700">
                    {{ $t('Rejection Reason (Optional)') }}
                </label>
                <Textarea
                    id="rejectReason"
                    v-model="rejectReason"
                    rows="4"
                    class="w-full"
                    :placeholder="$t('Describe what needs to be changed or improved...')"
                />
                <small class="text-surface-600">
                    {{ $t('You can reject content without providing a reason, but feedback helps improve the revision process.') }}
                </small>
            </div>
        </div>

        <template #footer>
            <div class="flex justify-end space-x-3">
                <Button
                    :label="$t('Cancel')"
                    icon="pi pi-times"
                    severity="secondary"
                    @click="closeRejectModal"
                    :disabled="rejectingContent"
                />
                <Button
                    :label="$t('Reject Content')"
                    icon="pi pi-times"
                    severity="danger"
                    @click="rejectContent"
                    :loading="rejectingContent"
                />
            </div>
        </template>
    </Dialog>
</template>

<style scoped>
.p-tag {
    font-weight: 600;
}
</style>
